/**
 * A2DashboardKPI.jsx · v2.8.9.4
 * ────────────────────────────────
 * Widget de KPIs del A2 para el Dashboard/AppLauncher.
 * Muestra ÚNICAMENTE métricas de éxito y contador de rescate manual.
 * El interruptor ON/OFF fue movido al panel de Cerebros (/admin/reglas-agentes).
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bot, CheckCircle2, ShieldAlert, ArrowUpRight, Settings } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function A2DashboardKPI() {
  const { hasPerm } = useAuth();
  const canView = hasPerm ? hasPerm("M32.a2.view") : false;

  const [state, setState] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/a2/autopilot/status");
      setState(data);
    } catch (_) { /* silencioso */ }
  }, []);

  useEffect(() => {
    if (!canView) return;
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [canView, load]);

  if (!canView || !state) return null;

  const on = !!state.enabled;
  const manual = state.manual_pending_total || 0;
  const events24h = state.events_24h || 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="a2-dashboard-kpi">
      {/* Estado A2 (info · sin toggle) */}
      <Link
        to="/admin/reglas-agentes"
        className={`rounded-xl p-4 flex items-center justify-between transition-all hover:shadow-md ${
          on ? "bg-emerald-500 text-white" : "bg-gray-200 text-gray-700"
        }`}
        data-testid="a2-status-tile"
      >
        <div>
          <div className="text-[10px] uppercase font-bold tracking-widest opacity-80">Motor A2</div>
          <div className="text-2xl font-bold" style={{ fontFamily: "Space Grotesk" }}>
            {on ? "ACTIVO" : "PAUSADO"}
          </div>
          <div className="text-[11px] opacity-90 mt-0.5 flex items-center gap-1">
            <Settings size={10} /> Configurar en Cerebros
          </div>
        </div>
        <Bot size={28} className="opacity-80" />
      </Link>

      {/* Éxitos 24h */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between" data-testid="a2-success-tile">
        <div>
          <div className="text-[10px] uppercase font-bold tracking-widest text-gray-500">Auto-agendamientos 24 h</div>
          <div className="text-2xl font-bold text-emerald-600" style={{ fontFamily: "Space Grotesk" }}>
            {events24h}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            {state.last_run_at
              ? `Último ciclo: ${new Date(state.last_run_at).toLocaleTimeString("es-EC", { timeZone: "America/Guayaquil", hour: "2-digit", minute: "2-digit" })}`
              : "Sin ciclos aún"}
          </div>
        </div>
        <CheckCircle2 size={28} className="text-emerald-500 opacity-80" />
      </div>

      {/* Rescate manual */}
      <Link
        to="/admin/a2-rescate"
        className={`rounded-xl p-4 flex items-center justify-between transition-all hover:shadow-md ${
          manual > 0 ? "bg-red-500 text-white" : "bg-white border border-gray-200 text-gray-700"
        }`}
        data-testid="a2-rescate-tile"
      >
        <div>
          <div className={`text-[10px] uppercase font-bold tracking-widest ${manual > 0 ? "opacity-80" : "text-gray-500"}`}>
            Rescate manual
          </div>
          <div className="text-2xl font-bold" style={{ fontFamily: "Space Grotesk" }}>
            {manual}
          </div>
          <div className={`text-[11px] mt-0.5 flex items-center gap-1 ${manual > 0 ? "opacity-90" : "text-gray-500"}`}>
            {manual > 0 ? "Ir a la bandeja" : "Ninguna pendiente"} <ArrowUpRight size={10} />
          </div>
        </div>
        <ShieldAlert size={28} className="opacity-80" />
      </Link>
    </div>
  );
}
